import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { APIService } from '../../utils/api.service';
import { DataService } from '../../utils/data.service';

export type AppUpdateState = 'unknown' | 'up-to-date' | 'optional' | 'required';

export interface AppVersionInfo {
  /** Versión del APK (vacía si la PWA corre en un navegador, fuera del APK). */
  apkVersionName: string;
  apkVersionCode: number | null;
  apkPackage: string;
  /** Commit / build de la PWA según version.json (lo escribe el pipeline de deploy). */
  webVersion: string;
  webBuild: string;
  updateState: AppUpdateState;
  latestVersionName: string | null;
}

interface LatestRelease {
  versionCode: number;
  versionName: string;
  requiredVersionCode: number;
}

const HEARTBEAT_INTERVAL_MS = 10 * 60 * 1000;

/**
 * Versiones del APK y de la web para mostrarlas siempre en pantalla y reportarlas al back
 * (login y heartbeat), de modo que la vista de dispositivos sepa qué versión usa cada agente.
 */
@Injectable({ providedIn: 'root' })
export class AppVersionService {
  private readonly infoSubject = new BehaviorSubject<AppVersionInfo>({
    apkVersionName: '',
    apkVersionCode: null,
    apkPackage: '',
    webVersion: '',
    webBuild: '',
    updateState: 'unknown',
    latestVersionName: null,
  });
  readonly info$ = this.infoSubject.asObservable();

  private initialized = false;
  private lastHeartbeat = 0;

  constructor(
    private apiService: APIService,
    private dataService: DataService,
    private zone: NgZone,
  ) {}

  get info(): AppVersionInfo {
    return this.infoSubject.value;
  }

  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    this.patch(this.readBridge());
    this.loadWebVersion();
    this.loadLatestRelease();
    this.startHeartbeat();
  }

  /** Campos de versión que se agregan al body del login de agente. */
  loginPayload(): Record<string, string | number> {
    const info = this.info;
    const bridge = window.PeripheralBridge;
    const payload: Record<string, string | number> = {};
    if (info.apkPackage) payload['appPackage'] = info.apkPackage;
    if (info.apkVersionCode != null) payload['appVersionCode'] = info.apkVersionCode;
    if (info.apkVersionName) payload['appVersionName'] = info.apkVersionName;
    if (info.webVersion) payload['webVersion'] = this.webVersionLabel();
    const model = this.safeCall(() => bridge?.getDeviceModel?.());
    if (model) payload['deviceModel'] = model;
    const android = this.safeCall(() => bridge?.getAndroidVersion?.());
    if (android) payload['androidVersion'] = android;
    return payload;
  }

  /** Pide al APK revisar si hay actualización (p.ej. justo después del login). */
  requestNativeUpdateCheck(): void {
    this.safeCall(() => window.PeripheralBridge?.checkForUpdates?.());
  }

  webVersionLabel(): string {
    const { webVersion, webBuild } = this.info;
    if (!webVersion) return '';
    const shortCommit = webVersion.slice(0, 7);
    return webBuild && webBuild !== '0' ? `#${webBuild} ${shortCommit}` : shortCommit;
  }

  private readBridge(): Partial<AppVersionInfo> {
    const bridge = window.PeripheralBridge;
    if (!bridge) return {};
    const versionName = this.safeCall(() => bridge.getAppVersion()) ?? '';
    const versionCode = this.safeCall(() => bridge.getAppVersionCode?.());
    const pkg = this.safeCall(() => bridge.getAppPackage?.()) ?? '';
    return {
      apkVersionName: versionName,
      apkVersionCode: typeof versionCode === 'number' ? versionCode : null,
      apkPackage: pkg,
    };
  }

  private async loadWebVersion(): Promise<void> {
    for (const url of ['version.json', 'assets/version.json']) {
      try {
        const response = await fetch(`${url}?t=${Date.now()}`, { cache: 'no-store' });
        if (!response.ok) continue;
        const data = await response.json();
        this.patch({
          webVersion: String(data.version ?? ''),
          webBuild: String(data.buildNumber ?? ''),
        });
        return;
      } catch {
        // Offline (copia embebida en el APK) o en desarrollo: se intenta la siguiente ruta.
      }
    }
  }

  private async loadLatestRelease(): Promise<void> {
    const { apkPackage, apkVersionCode } = this.info;
    if (!apkPackage || apkVersionCode == null) return;
    try {
      const latest: LatestRelease = await firstValueFrom(
        this.apiService.get(`app-releases/latest?package=${encodeURIComponent(apkPackage)}`),
      );
      const state: AppUpdateState =
        apkVersionCode < latest.requiredVersionCode
          ? 'required'
          : apkVersionCode < latest.versionCode
            ? 'optional'
            : 'up-to-date';
      this.patch({ updateState: state, latestVersionName: latest.versionName });
    } catch {
      // 404 (sin versiones publicadas) o sin red: el indicador queda neutro.
    }
  }

  private startHeartbeat(): void {
    const beat = () => this.sendHeartbeat();
    this.zone.runOutsideAngular(() => {
      setInterval(beat, HEARTBEAT_INTERVAL_MS);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') beat();
      });
    });
    // Primer latido al abrir la app (si ya hay sesión).
    setTimeout(beat, 5000);
  }

  private async sendHeartbeat(): Promise<void> {
    const session = this.dataService.getData('sessionData');
    const androidId = this.safeCall(() => window.PeripheralBridge?.getAndroidId());
    if (!session?.token || !androidId) return;
    // Evita ráfagas al alternar apps rápidamente.
    if (Date.now() - this.lastHeartbeat < 60 * 1000) return;
    this.lastHeartbeat = Date.now();

    const info = this.info;
    const body: Record<string, string | number> = {
      event: 'HEARTBEAT',
      androidId,
      ...this.loginPayload(),
    };
    // El DTO de reportes usa nombres distintos a los del login.
    body['package'] = info.apkPackage;
    if (info.apkVersionCode != null) body['versionCode'] = info.apkVersionCode;
    if (info.apkVersionName) body['versionName'] = info.apkVersionName;
    for (const key of ['appPackage', 'appVersionCode', 'appVersionName']) delete body[key];
    if (!body['package']) delete body['package'];

    try {
      await firstValueFrom(this.apiService.post('app-releases/reports', body));
    } catch {
      // Sin red: se reintenta en el próximo latido.
    }
  }

  private patch(changes: Partial<AppVersionInfo>): void {
    this.zone.run(() => this.infoSubject.next({ ...this.infoSubject.value, ...changes }));
  }

  private safeCall<T>(fn: () => T): T | undefined {
    try {
      return fn();
    } catch {
      return undefined;
    }
  }
}
